import { Button, Form, Input, Radio, Space } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { AdminDTO, Status } from '../service';

const StyledForm = styled(Form<EditAdminFormValues>)`
  .ant-form-item-label {
    font-weight: 500;
  }
`;

export type EditAdminFormValues = {
  email: string;
  first_name: string;
  last_name: string;
  role: 'root_admin' | 'tenant_admin';
  tenant_id?: string;
  status: Status;
};

export interface EditAdminFormProps {
  admin: AdminDTO;
  onSubmit?: (values: EditAdminFormValues) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
  readonly?: boolean;
}

const EditAdminForm: React.FC<EditAdminFormProps> = ({
  admin,
  onSubmit,
  onCancel,
  submitting = false,
  readonly = false,
}) => {
  const [form] = Form.useForm<EditAdminFormValues>();
  const [role, setRole] = React.useState<'root_admin' | 'tenant_admin'>(admin.role);

  // Initialize form with admin data
  React.useEffect(() => {
    form.setFieldsValue({
      email: admin.email,
      first_name: admin.first_name,
      last_name: admin.last_name,
      role: admin.role,
      tenant_id: 'tenant_id' in admin ? admin.tenant_id : undefined,
      status: admin.status,
    });
  }, [admin, form]);

  const handleSubmit = async (values: EditAdminFormValues) => {
    if (!onSubmit) return;
    await onSubmit(values);
  };

  return (
    <>
      <StyledForm
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={readonly}
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please input email!' },
            { type: 'email', message: 'Please enter a valid email!' }
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>

        <Form.Item
          label="First Name"
          name="first_name"
          rules={[{ required: true, message: 'Please input first name!' }]}
        >
          <Input placeholder="Enter first name" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="last_name"
          rules={[{ required: true, message: 'Please input last name!' }]}
        >
          <Input placeholder="Enter last name" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Please select role!' }]}
        >
          <Radio.Group onChange={(e) => setRole(e.target.value)} disabled={readonly}>
            <Radio value="root_admin">Root Admin</Radio>
            <Radio value="tenant_admin">Tenant Admin</Radio>
          </Radio.Group>
        </Form.Item>

        {role === 'tenant_admin' && (
          <Form.Item
            label="Tenant ID"
            name="tenant_id"
            rules={[{ required: true, message: 'Please input tenant ID!' }]}
          >
            <Input placeholder="Enter tenant ID" />
          </Form.Item>
        )}

        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: 'Please select status!' }]}
        >
          <Radio.Group disabled={readonly}>
            <Radio value="active">Active</Radio>
            <Radio value="inactive">Inactive</Radio>
          </Radio.Group>
        </Form.Item>

        {!readonly && (
          <Form.Item style={{ marginTop: 24, marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Update
              </Button>
            </Space>
          </Form.Item>
        )}
      </StyledForm>
      {readonly && (
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button onClick={onCancel}>Close</Button>
        </div>
      )}
    </>
  );
};

export default EditAdminForm; 